/**
 * The price, edited beside the page.
 *
 * Three parts, in the order the customer reads them. First, **line items or
 * options** — one or the other, because options take the place of line items:
 * each option is priced from its own lines and the customer picks one. Then
 * the **optional extras**, rows the customer ticks on top of either. A line's
 * part *is* its type — a line under Premium is `optionKey: premium`, a line
 * under extras is `isOptional` — so there is no per-row "type" select to get
 * wrong. Each line is a small card rather than a table row because the
 * inspector is a column, and the same layout serves the phone's sheet.
 * Decisions 80, 81 and 82.
 */
import { useQuery } from '@tanstack/react-query'
import { defaultOptionId } from '@fieldops/shared'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Button, Field, Input, Select, Textarea } from '../ui.js'
import {
  blankLine,
  centsOf,
  dollarsOf,
  draftTotals,
  incTaxPrice,
  lineFigures,
  newKey,
  type DraftLine,
  type DraftOption,
  type ProposalDraft,
} from './draft.js'
import type { DraftApply } from './useDraftHistory.js'

export const MAX_OPTIONS = 6
export const PROSE_HINT = 'A blank line starts a new paragraph; start a line with "- " for a bullet.'

export function PricingInspector({
  draft,
  apply,
  mode,
  clientId,
  focusOption,
}: {
  draft: ProposalDraft
  apply: DraftApply
  mode: 'quote' | 'template'
  clientId: string | null
  /** A package clicked on the page opens it here. */
  focusOption?: string | null
}) {
  const trpc = useTRPC()
  const taxRates = useQuery(trpc.catalog.taxRates.queryOptions())
  const [openOption, setOpenOption] = useState<string | null>(focusOption ?? draft.options[0]?.key ?? null)

  useEffect(() => {
    if (focusOption) setOpenOption(focusOption)
  }, [focusOption])

  const rates = useMemo(
    () => new Map((taxRates.data ?? []).map((rate) => [rate.id, rate.rateBasisPoints])),
    [taxRates.data],
  )
  const lineDefaults = useLineDefaults()

  // A package that was deleted leaves nothing open; open the first there is.
  useEffect(() => {
    if (!draft.options.some((option) => option.key === openOption)) setOpenOption(draft.options[0]?.key ?? null)
  }, [draft.options, openOption])

  const totals = draftTotals(draft)
  const hasPackages = draft.options.length > 0
  const included = draft.lines.filter((line) => !line.isOptional && line.optionKey === null)
  const extras = draft.lines.filter((line) => line.isOptional)
  const currentOption = draft.options.find((option) => option.key === openOption) ?? null
  const optionLines = currentOption
    ? draft.lines.filter((line) => !line.isOptional && line.optionKey === currentOption.key)
    : []

  const optionName = (count: number) =>
    count === 0 ? 'Standard' : count === 1 ? 'Premium' : `Option ${count + 1}`

  /** Line items become the first option's lines, so nothing typed is lost. */
  const switchToOptions = () => {
    const first: DraftOption = { key: newKey('o'), name: optionName(0), summary: '', isRecommended: false }
    const second: DraftOption = { key: newKey('o'), name: optionName(1), summary: '', isRecommended: false }
    apply((d) => ({
      ...d,
      options: [first, second],
      lines: d.lines.map((line) =>
        !line.isOptional && line.optionKey === null ? { ...line, optionKey: first.key } : line,
      ),
    }))
    setOpenOption(first.key)
  }

  /** Back to line items: the default option's lines stay, the others' go. */
  const switchToLineItems = () => {
    const keep = defaultOptionId(
      draft.options.map((option, index) => ({ id: option.key, isRecommended: option.isRecommended, sortOrder: index })),
    )
    apply((d) => ({
      ...d,
      options: [],
      lines: d.lines
        .filter((line) => line.isOptional || line.optionKey === null || line.optionKey === keep)
        .map((line) => (line.optionKey ? { ...line, optionKey: null } : line)),
    }))
  }

  const addOption = () => {
    const option: DraftOption = { key: newKey('o'), name: optionName(draft.options.length), summary: '', isRecommended: false }
    apply((d) => ({ ...d, options: [...d.options, option] }))
    setOpenOption(option.key)
  }

  const patchOption = (key: string, next: Partial<DraftOption>, group?: string) =>
    apply(
      (d) => ({
        ...d,
        options: d.options.map((option) => {
          if (option.key === key) return { ...option, ...next }
          // One recommendation at most: ticking one clears the others.
          return next.isRecommended ? { ...option, isRecommended: false } : option
        }),
      }),
      group,
    )

  const removeOption = (key: string) => {
    if (draft.options.length === 1) {
      switchToLineItems()
      return
    }
    apply((d) => ({
      ...d,
      options: d.options.filter((option) => option.key !== key),
      lines: d.lines
        .filter((line) => line.isOptional || line.optionKey !== key)
        // An extra offered only with it is offered with any option instead.
        .map((line) => (line.optionKey === key ? { ...line, optionKey: null } : line)),
    }))
  }

  const patchLine = (key: string, next: Partial<DraftLine>, group?: string) =>
    apply((d) => ({ ...d, lines: d.lines.map((line) => (line.key === key ? { ...line, ...next } : line)) }), group)

  const removeLine = (key: string) => apply((d) => ({ ...d, lines: d.lines.filter((line) => line.key !== key) }))

  const addLine = (place: Pick<DraftLine, 'isOptional' | 'optionKey'>) => (patch: Partial<DraftLine> = {}) =>
    apply((d) => ({
      ...d,
      lines: [
        ...d.lines,
        blankLine(lineDefaults, {
          ...place,
          ...(mode === 'template' ? { unitPrice: null } : {}),
          ...patch,
        }),
      ],
    }))

  const cards = (lines: DraftLine[], isExtras: boolean) => (
    <LineCards
      lines={lines}
      extras={isExtras}
      options={draft.options}
      rates={taxRates.data ?? []}
      rateOf={(id) => (id ? (rates.get(id) ?? 0) : 0)}
      mode={mode}
      onPatch={patchLine}
      onRemove={removeLine}
    />
  )

  const adders = (label: string, onAdd: (patch?: Partial<DraftLine>) => void) => (
    <AddLineControls
      label={label}
      clientId={clientId}
      mode={mode}
      onAdd={onAdd}
      lineDefaults={lineDefaults}
      rateOf={(id) => rates.get(id) ?? 0}
    />
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div role="radiogroup" aria-label="How the price is set out" className="grid grid-cols-2 gap-1 rounded-lg bg-ink-100 p-1">
          <LayoutChoice checked={!hasPackages} onClick={() => (hasPackages ? switchToLineItems() : undefined)}>
            Line items
          </LayoutChoice>
          <LayoutChoice checked={hasPackages} onClick={() => (hasPackages ? undefined : switchToOptions())}>
            Options
          </LayoutChoice>
        </div>
        <p className="text-xs text-ink-500">
          {hasPackages
            ? 'Big cards the customer picks one of. Each option is priced from its own lines, in place of line items.'
            : 'The work, priced line by line. Choose Options to offer packages to pick between instead.'}
        </p>
      </div>

      {hasPackages ? (
        <PricingGroup title="Options">
          <div role="tablist" aria-label="Options" className="flex flex-wrap items-center gap-1">
            {draft.options.map((option) => (
              <Tab key={option.key} active={option.key === openOption} onClick={() => setOpenOption(option.key)}>
                {option.name.trim() || 'Unnamed option'}
                {option.isRecommended ? <span className="ml-1 text-brand-700">★</span> : null}
              </Tab>
            ))}
            {draft.options.length < MAX_OPTIONS ? (
              <button
                type="button"
                onClick={addOption}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-ink-600 ring-1 ring-ink-200 ring-dashed hover:text-ink-900 hover:ring-ink-400"
              >
                <Icon name="plus" className="size-3" />
                Add an option
              </button>
            ) : null}
          </div>

          {currentOption ? (
            <>
              <div className="flex flex-col gap-3 rounded-xl bg-ink-50 p-3 ring-1 ring-ink-200">
                <Field label="Option name" htmlFor={`option-${currentOption.key}-name`}>
                  <Input
                    id={`option-${currentOption.key}-name`}
                    value={currentOption.name}
                    onChange={(event) =>
                      patchOption(currentOption.key, { name: event.target.value }, `option-name-${currentOption.key}`)
                    }
                  />
                </Field>
                <Field label="What this option is" htmlFor={`option-${currentOption.key}-summary`} hint={PROSE_HINT}>
                  <Textarea
                    id={`option-${currentOption.key}-summary`}
                    rows={3}
                    value={currentOption.summary}
                    onChange={(event) =>
                      patchOption(currentOption.key, { summary: event.target.value }, `option-summary-${currentOption.key}`)
                    }
                  />
                </Field>
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm text-ink-700">
                    <input
                      type="checkbox"
                      className="size-4 accent-ink-900"
                      checked={currentOption.isRecommended}
                      onChange={(event) => patchOption(currentOption.key, { isRecommended: event.target.checked })}
                    />
                    Recommended
                  </label>
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeOption(currentOption.key)}>
                    Remove option
                  </Button>
                </div>
              </div>
              {cards(optionLines, false)}
              {adders(`Add a line to ${currentOption.name.trim() || 'this option'}`, addLine({ isOptional: false, optionKey: currentOption.key }))}
            </>
          ) : null}

          {included.length > 0 ? (
            <div className="flex flex-col gap-2 rounded-xl bg-amber-50 p-3 ring-1 ring-amber-200">
              <p className="text-xs text-amber-900">
                In every option. Proposals set out this way before options replaced line items still show these
                above the options; move them into an option or remove them.
              </p>
              {cards(included, false)}
            </div>
          ) : null}
        </PricingGroup>
      ) : (
        <PricingGroup title="Line items">
          {cards(included, false)}
          {adders('Add a line item', addLine({ isOptional: false, optionKey: null }))}
        </PricingGroup>
      )}

      <PricingGroup
        title="Optional extras"
        hint="Rows with a tick box. Not in the price until the customer ticks one."
      >
        {cards(extras, true)}
        {adders('Add an extra', addLine({ isOptional: true, optionKey: null }))}
      </PricingGroup>

      <PricingSummary draft={draft} totals={totals} />
    </div>
  )
}

/** The tax a new line starts with: the default rate, else the first there is. */
export function useLineDefaults(): { taxRateId: string | null; taxRateBasisPoints: number } {
  const trpc = useTRPC()
  const taxRates = useQuery(trpc.catalog.taxRates.queryOptions())
  const rate = taxRates.data?.find((entry) => entry.isDefault) ?? taxRates.data?.[0] ?? null
  return { taxRateId: rate?.id ?? null, taxRateBasisPoints: rate?.rateBasisPoints ?? 0 }
}

function PricingGroup({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-2.5 border-t border-ink-200 pt-4">
      <div>
        <h4 className="text-[11px] font-bold tracking-[0.08em] text-ink-700 uppercase">{title}</h4>
        {hint ? <p className="mt-0.5 text-xs text-ink-500">{hint}</p> : null}
      </div>
      {children}
    </section>
  )
}

function LayoutChoice({ checked, onClick, children }: { checked: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors',
        checked ? 'bg-white text-ink-900 shadow-sm ring-1 ring-ink-200' : 'text-ink-600 hover:text-ink-900',
      )}
    >
      {children}
    </button>
  )
}

/** A blank line, a catalog item or a service, added to one part of the price. */
function AddLineControls({
  label,
  clientId,
  mode,
  onAdd,
  lineDefaults,
  rateOf,
}: {
  label: string
  clientId: string | null
  mode: 'quote' | 'template'
  onAdd: (patch?: Partial<DraftLine>) => void
  lineDefaults: { taxRateId: string | null; taxRateBasisPoints: number }
  rateOf: (taxRateId: string) => number
}) {
  return (
    <div className="flex flex-col gap-2">
      <Button type="button" size="sm" icon={<Icon name="plus" className="size-3.5" />} onClick={() => onAdd()}>
        {label}
      </Button>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <CatalogSearch
          clientId={clientId}
          label={`${label} from the catalog`}
          onPick={(material) =>
            onAdd({
              description: material.name,
              materialId: material.id,
              unitPrice: mode === 'template' ? null : material.unitPrice,
              taxRateId: material.taxRateId ?? lineDefaults.taxRateId,
              taxRateBasisPoints: material.taxRateId ? rateOf(material.taxRateId) : lineDefaults.taxRateBasisPoints,
              source: material.code ? `Catalog ${material.code}` : 'Catalog',
            })
          }
        />
        <ServicePicker
          label={`${label} as a service`}
          onPick={(service) =>
            onAdd({
              description: service.name,
              serviceTypeId: service.id,
              ...incTaxPrice(
                mode === 'template' ? null : service.defaultPriceIncTax,
                lineDefaults.taxRateBasisPoints,
              ),
              source: 'Service — put on the job when accepted',
            })
          }
        />
      </div>
    </div>
  )
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1 text-[13px] font-medium transition-colors',
        active ? 'bg-brand-800 text-white' : 'text-ink-600 ring-1 ring-ink-200 hover:text-ink-900 hover:ring-ink-400',
      )}
    >
      {children}
    </button>
  )
}

/**
 * Dollars typed as text. The draft only ever holds minor units, so this keeps
 * what was typed while the field has focus and reformats on the way out —
 * otherwise "12." would be rewritten as "12" under the cursor.
 */
export function PriceInput({
  id,
  value,
  onChange,
  placeholder,
  className,
  label,
}: {
  id?: string
  value: number | null
  onChange: (cents: number | null) => void
  placeholder?: string
  className?: string
  label?: string
}) {
  const [text, setText] = useState(dollarsOf(value))
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) setText(dollarsOf(value))
  }, [value])

  return (
    <Input
      id={id}
      aria-label={label}
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      className={className}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        setText(dollarsOf(value))
      }}
      onChange={(event) => {
        setText(event.target.value)
        onChange(centsOf(event.target.value))
      }}
    />
  )
}

function LineCards({
  lines,
  extras,
  options,
  rates,
  rateOf,
  mode,
  onPatch,
  onRemove,
}: {
  lines: DraftLine[]
  extras: boolean
  options: DraftOption[]
  rates: Array<{ id: string; name: string; rateBasisPoints: number }>
  rateOf: (id: string | null) => number
  mode: 'quote' | 'template'
  onPatch: (key: string, next: Partial<DraftLine>, group?: string) => void
  onRemove: (key: string) => void
}) {
  if (lines.length === 0) {
    return <p className="rounded-xl bg-ink-50 px-3 py-4 text-center text-sm text-ink-500">Nothing here yet.</p>
  }

  return (
    <ul className="flex flex-col gap-2">
      {lines.map((line, index) => {
        const figures = lineFigures(line)
        return (
          <li key={line.key} className="flex flex-col gap-2 rounded-xl bg-white p-2.5 ring-1 ring-ink-200">
            <div className="flex items-start gap-1.5">
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <Input
                  aria-label={`Line ${index + 1} description`}
                  value={line.description}
                  placeholder="Description"
                  onChange={(event) => onPatch(line.key, { description: event.target.value }, `line-${line.key}-description`)}
                />
                {line.source ? <span className="truncate px-1 text-[11px] text-ink-500">{line.source}</span> : null}
              </span>
              <button
                type="button"
                aria-label={`Remove line ${index + 1}`}
                onClick={() => onRemove(line.key)}
                className="mt-1 grid size-7 shrink-0 place-items-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-800"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-[56px_1fr_1fr] gap-1.5">
              <Input
                aria-label={`Line ${index + 1} quantity`}
                inputMode="decimal"
                className="text-right"
                value={line.quantity}
                onChange={(event) => onPatch(line.key, { quantity: event.target.value }, `line-${line.key}-quantity`)}
              />
              <PriceInput
                label={`Line ${index + 1} unit price`}
                className="text-right"
                value={line.unitPrice}
                placeholder={mode === 'template' ? 'Client price' : 'Unit ex GST'}
                onChange={(cents) =>
                  onPatch(
                    line.key,
                    { unitPrice: mode === 'template' ? cents : (cents ?? 0), unitPriceIncTax: null },
                    `line-${line.key}-price`,
                  )
                }
              />
              <Select
                aria-label={`Line ${index + 1} tax`}
                value={line.taxRateId ?? ''}
                onChange={(event) => {
                  const id = event.target.value || null
                  // A price typed inc GST stays what was typed; its ex-GST cent moves.
                  onPatch(line.key, {
                    taxRateId: id,
                    taxRateBasisPoints: rateOf(id),
                    ...(line.unitPriceIncTax != null ? incTaxPrice(line.unitPriceIncTax, rateOf(id)) : {}),
                  })
                }}
              >
                <option value="">No tax</option>
                {rates.map((rate) => (
                  <option key={rate.id} value={rate.id}>
                    {rate.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-center gap-2">
              {extras && options.length > 0 ? (
                <Select
                  aria-label={`Line ${index + 1} offered with`}
                  className="min-w-0 flex-1"
                  value={line.optionKey ?? ''}
                  onChange={(event) => onPatch(line.key, { optionKey: event.target.value || null })}
                >
                  <option value="">With any option</option>
                  {options.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.name || 'Unnamed option'} only
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="flex-1" />
              )}
              <span className="text-[13px] font-semibold tabular-nums text-ink-900">
                {line.unitPrice === null ? 'Priced per client' : `${money(figures.lineTotalIncTax)} inc`}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function CatalogSearch({
  clientId,
  label,
  onPick,
}: {
  clientId: string | null
  label: string
  onPick: (material: { id: string; code: string | null; name: string; unitPrice: number; taxRateId: string | null }) => void
}) {
  const trpc = useTRPC()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const term = q.trim()

  const results = useQuery({
    ...trpc.catalog.searchMaterials.queryOptions({ q: term, limit: 8, ...(clientId ? { clientId } : {}) }),
    enabled: open && term.length >= 2,
  })

  return (
    <div className="relative">
      <Input
        aria-label={label}
        placeholder="Search the catalog…"
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(event) => {
          setQ(event.target.value)
          setOpen(true)
        }}
      />
      {open && term.length >= 2 ? (
        <ul
          role="listbox"
          aria-label="Catalog items"
          className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-ink-200"
        >
          {(results.data ?? []).length === 0 ? (
            <li className="px-3 py-2 text-xs text-ink-500">{results.isFetching ? 'Searching…' : 'Nothing matches.'}</li>
          ) : (
            results.data!.map((material) => (
              <li key={material.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onPick(material)
                    setQ('')
                    setOpen(false)
                  }}
                  className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-ink-50"
                >
                  <span className="min-w-0 flex-1 truncate text-ink-900">{material.name}</span>
                  <span className="shrink-0 tabular-nums text-ink-500">{money(material.unitPrice)}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}

function ServicePicker({
  label,
  onPick,
}: {
  label: string
  onPick: (service: { id: string; name: string; defaultPriceIncTax: number | null }) => void
}) {
  const trpc = useTRPC()
  const services = useQuery(trpc.catalog.serviceTypes.queryOptions())

  if (!services.data || services.data.length === 0) return null

  return (
    <Select
      aria-label={label}
      value=""
      onChange={(event) => {
        const service = services.data.find((entry) => entry.id === event.target.value)
        if (service) onPick(service)
      }}
    >
      <option value="">Add a service…</option>
      {services.data.map((service) => (
        <option key={service.id} value={service.id}>
          {service.name}
        </option>
      ))}
    </Select>
  )
}

function PricingSummary({ draft, totals }: { draft: ProposalDraft; totals: ReturnType<typeof draftTotals> }) {
  if (draft.options.length === 0) {
    return (
      <dl className="flex flex-col gap-1 border-t border-ink-200 pt-3 text-[13px]">
        <SummaryRow label="Subtotal ex GST" value={totals.chosen.subtotalExTax} />
        <SummaryRow label="GST" value={totals.chosen.totalTax} />
        <SummaryRow label="Total" value={totals.chosen.totalIncTax} strong />
        <dd className="text-[11px] text-ink-500">Extras are not in the total until chosen.</dd>
      </dl>
    )
  }

  return (
    <div className="border-t border-ink-200 pt-3">
      <p className="text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase">Each option comes to</p>
      <ul className="mt-1 flex flex-wrap gap-2">
        {draft.options.map((option) => {
          const price = totals.packages.find((entry) => entry.optionId === option.key)
          return (
            <li key={option.key} className="rounded-md bg-ink-50 px-3 py-2 ring-1 ring-ink-200">
              <span className="block text-xs text-ink-600">{option.name || 'Unnamed option'}</span>
              <span className="text-sm font-semibold tabular-nums text-ink-900">{money(price?.totalIncTax ?? 0)}</span>
            </li>
          )
        })}
      </ul>
      <p className="mt-1 text-[11px] text-ink-500">
        Inc GST, before extras. The quote's own total is the{' '}
        {draft.options.some((option) => option.isRecommended) ? 'recommended' : 'first'} option.
      </p>
    </div>
  )
}

function SummaryRow({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between', strong && 'text-sm font-semibold text-ink-900')}>
      <dt className={strong ? undefined : 'text-ink-500'}>{label}</dt>
      <dd className="tabular-nums">{money(value)}</dd>
    </div>
  )
}
